import { getEmployeesForMetrics } from './calculations.js'

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, finite(value)))
const money = (value) => Math.max(0, finite(value))
const sumBy = (rows, key) => rows.reduce((sum, row) => sum + money(row[key]), 0)

export const DEFAULT_PROPOSAL_REPORT_SETTINGS = Object.freeze({
  marginalTaxRatePercent: 30,
  projectionYears: 10,
  annualContributionGrowthPercent: 0,
  interestCreditAssumptionPercent: null,
})

export function resolveProposalReportSettings(reportConfig = {}, proposal = null) {
  const configured = isObject(reportConfig?.proposal) ? reportConfig.proposal : {}
  const proposalInputs = isObject(proposal?.reportInputs) ? proposal.reportInputs : {}
  const choose = (configuredValue, proposalValue, fallback) => (
    configuredValue === '' || configuredValue === null || configuredValue === undefined
      ? (proposalValue === '' || proposalValue === null || proposalValue === undefined ? fallback : proposalValue)
      : configuredValue
  )
  const interestValue = choose(
    configured.interestCreditAssumptionPercent,
    proposalInputs.interestCreditAssumptionPercent,
    null,
  )
  return {
    marginalTaxRatePercent: clamp(choose(
      configured.marginalTaxRatePercent,
      proposalInputs.marginalTaxRatePercent,
      DEFAULT_PROPOSAL_REPORT_SETTINGS.marginalTaxRatePercent,
    ), 0, 60),
    projectionYears: Math.round(clamp(choose(
      configured.projectionYears,
      proposalInputs.projectionYears,
      DEFAULT_PROPOSAL_REPORT_SETTINGS.projectionYears,
    ), 1, 30)),
    annualContributionGrowthPercent: clamp(choose(
      configured.annualContributionGrowthPercent,
      proposalInputs.salaryGrowthPercent,
      DEFAULT_PROPOSAL_REPORT_SETTINGS.annualContributionGrowthPercent,
    ), 0, 15),
    interestCreditAssumptionPercent: interestValue === null
      ? null : clamp(interestValue, 0, 15),
  }
}

const normalizeFundingCorridor = (proposal) => {
  const source = isObject(proposal?.reportInputs?.fundingCorridor)
    ? proposal.reportInputs.fundingCorridor : {}
  const nullableMoney = (value) => value === '' || value === null || value === undefined
    ? null : money(value)
  const funding = {
    minimum: nullableMoney(source.minimum),
    recommended: nullableMoney(source.recommended),
    maximum: nullableMoney(source.maximum),
    source: String(source.source || '').trim(),
    asOf: String(source.asOf || '').trim(),
    note: String(source.note || '').trim(),
  }
  funding.complete = [funding.minimum, funding.recommended, funding.maximum]
    .every((value) => value !== null) && Boolean(funding.source)
  funding.recommendedAvailable = funding.recommended !== null && Boolean(funding.source)
  return funding
}

const employeeRow = (employee) => {
  const own401k = money(employee.totalEmployeeContribution) + money(employee.afterTaxContribution)
  const matchSafeHarbor = money(employee.matchAmount) + money(employee.necAmount) + money(employee.qnecAmount)
  const profitSharing = money(employee.profitSharingAmount)
  const cashBalance = money(employee.cashBalanceCredit)
  const employerAllocation = matchSafeHarbor + profitSharing + cashBalance
  return {
    id: String(employee.id || ''),
    name: String(employee.name || employee.id || 'Participant'),
    age: Number.isFinite(Number(employee.age)) ? Number(employee.age) : null,
    pay: money(employee.cappedCompensation),
    own401k,
    matchSafeHarbor,
    profitSharing,
    cashBalance,
    dcEmployer: matchSafeHarbor + profitSharing,
    employerAllocation,
    totalRetirementAllocation: own401k + employerAllocation,
    employerPercentOfPay: money(employee.cappedCompensation) > 0
      ? employerAllocation / money(employee.cappedCompensation) * 100 : 0,
    isHCE: employee.isHCE === true,
    isExplicitOwner: employee.isFivePercentOwner === true || finite(employee.ownershipPercent) > 5,
    cashBalanceOpeningBalance: money(employee.cashBalanceOpeningBalance),
    serviceYears: Number.isFinite(Number(employee.serviceYears)) ? Number(employee.serviceYears) : null,
    eligibilityStatus: String(employee.eligibility?.status || 'unknown'),
  }
}

const subtotal = (rows) => ({
  pay: sumBy(rows, 'pay'),
  own401k: sumBy(rows, 'own401k'),
  matchSafeHarbor: sumBy(rows, 'matchSafeHarbor'),
  profitSharing: sumBy(rows, 'profitSharing'),
  cashBalance: sumBy(rows, 'cashBalance'),
  dcEmployer: sumBy(rows, 'dcEmployer'),
  employerAllocation: sumBy(rows, 'employerAllocation'),
  totalRetirementAllocation: sumBy(rows, 'totalRetirementAllocation'),
})

const roleRows = (rows, proposal) => {
  let ownerIds = new Set(rows.filter((row) => row.isExplicitOwner).map((row) => row.id))
  if (!ownerIds.size) {
    const targetCount = Math.max(0, Number(proposal?.goal?.targetCount) || 0)
    if (targetCount > 0) {
      ownerIds = new Set(rows
        .filter((row) => row.isHCE)
        .sort((a, b) => b.employerAllocation - a.employerAllocation || b.pay - a.pay)
        .slice(0, targetCount)
        .map((row) => row.id))
    }
  }
  return rows.map((row) => ({
    ...row,
    role: ownerIds.has(row.id) ? 'owner' : (row.isHCE ? 'hce' : 'team'),
  })).sort((a, b) => {
    const order = { owner: 0, hce: 1, team: 2 }
    return order[a.role] - order[b.role]
      || b.totalRetirementAllocation - a.totalRetirementAllocation
      || a.name.localeCompare(b.name)
  })
}

// The first line is employee money. The remaining lines are modeled employer
// allocations; cash-balance benefit credits stay identifiable because they
// are not the employer's required deposit.
const sourceBreakdown = (rows) => [
  { key: 'own401k', label: 'Employee savings', value: sumBy(rows, 'own401k') },
  { key: 'matchSafeHarbor', label: 'Match, NEC & QNEC', value: sumBy(rows, 'matchSafeHarbor') },
  { key: 'profitSharing', label: 'Profit sharing', value: sumBy(rows, 'profitSharing') },
  { key: 'cashBalance', label: 'Cash balance (pension)', value: sumBy(rows, 'cashBalance') },
].map((source) => ({
  ...source,
  owners: sumBy(rows.filter((row) => row.role === 'owner'), source.key),
  team: sumBy(rows.filter((row) => row.role !== 'owner'), source.key),
})).filter((source) => source.value > 0)

const projectionRows = ({ owners, settings, enginePlan, planYear }) => {
  const interestCredit = enginePlan?.cashBalance?.interestCredit || {}
  const planRate = interestCredit.type === 'index'
    ? finite(interestCredit.assumptionRate, finite(enginePlan?.cashBalance?.interestCreditRate, 5))
    : finite(interestCredit.rate, finite(enginePlan?.cashBalance?.interestCreditRate, 5))
  const interestRatePercent = settings.interestCreditAssumptionPercent === null
    ? planRate : settings.interestCreditAssumptionPercent
  const interestRate = interestRatePercent / 100
  const growth = 1 + settings.annualContributionGrowthPercent / 100
  return owners.filter((row) => row.cashBalance > 0).map((row) => {
    let opening = row.cashBalanceOpeningBalance
    let annualCredit = row.cashBalance
    const years = []
    for (let index = 0; index < settings.projectionYears; index += 1) {
      const interest = Math.round(opening * interestRate)
      const ending = opening + annualCredit + interest
      years.push({
        year: (Number(planYear) || new Date().getFullYear()) + index,
        age: row.age === null ? null : row.age + index,
        opening,
        businessContribution: annualCredit,
        interest,
        ending,
      })
      opening = ending
      annualCredit = Math.round(annualCredit * growth)
    }
    return {
      id: row.id,
      name: row.name,
      currentAge: row.age,
      openingBalance: row.cashBalanceOpeningBalance,
      annualCredit: row.cashBalance,
      interestRatePercent,
      annualContributionGrowthPercent: settings.annualContributionGrowthPercent,
      years,
      endingBalance: years.at(-1)?.ending || row.cashBalanceOpeningBalance,
    }
  })
}

const alternative = ({ id, label, rows, includeProfitSharing, includeCashBalance, taxRate, funding }) => {
  const values = rows.map((row) => {
    const employer = row.matchSafeHarbor
      + (includeProfitSharing ? row.profitSharing : 0)
      + (includeCashBalance ? row.cashBalance : 0)
    return { ...row, alternativeEmployer: employer }
  })
  const ownerBenefit = values.filter((row) => row.role === 'owner')
    .reduce((sum, row) => sum + row.alternativeEmployer, 0)
  const teamCost = values.filter((row) => row.role !== 'owner')
    .reduce((sum, row) => sum + row.alternativeEmployer, 0)
  const modeledAllocation = ownerBenefit + teamCost
  const dcCost = values.reduce((sum, row) => sum + row.matchSafeHarbor
    + (includeProfitSharing ? row.profitSharing : 0), 0)
  const hasCb = includeCashBalance && sumBy(rows, 'cashBalance') > 0
  const deductibleContribution = hasCb
    ? (funding.recommendedAvailable ? dcCost + funding.recommended : null)
    : dcCost
  return {
    id,
    label,
    ownerBenefit,
    teamCost,
    modeledAllocation,
    deductibleContribution,
    estimatedTaxSavings: deductibleContribution === null ? null : deductibleContribution * taxRate,
    ownerSharePercent: modeledAllocation > 0 ? ownerBenefit / modeledAllocation * 100 : 0,
    includesCashBalance: hasCb,
  }
}

// Both exports tell the same story: one group against everyone else. That group
// is the owners when the census identifies them (or the proposal named targets);
// with none flagged, "owners vs staff" would be meaningless, so the highly paid
// take that place. "Everyone else" is always the rest of the workforce, never a
// bucket that still contains the highlighted group.
export function describeProposalContrast(model) {
  const groups = model?.groups || []
  const rows = model?.rows || []
  const totals = model?.totals || {}
  const ownerGroup = groups.find((group) => group.key === 'owners')
  const highlight = ownerGroup || groups.find((group) => group.key === 'hces') || null
  const highlightIds = new Set((highlight?.rows || []).map((row) => row.id))
  const restRows = rows.filter((row) => !highlightIds.has(row.id))
  const value = (key) => money(highlight?.subtotal?.[key])
  return {
    isOwners: Boolean(ownerGroup),
    highlightLabel: ownerGroup ? 'owners' : 'highly paid employees',
    restLabel: 'everyone else',
    highlightCount: highlight?.rows.length || 0,
    value,
    restValue: (key) => money(totals[key]) - value(key),
    restRecipientCount: restRows.filter((row) => row.employerAllocation > 0).length,
  }
}

// Side by side, the lines a business owner actually decides on: what the plan
// costs the company, how much of that lands with the owners, and what it works
// out to per person. One entry per plan design being compared; with a single
// design it is still the honest one-column answer to "what does this cost".
export function compareProposalModels(entries = []) {
  const columns = entries
    .filter((entry) => entry?.model?.available)
    .map(({ name, model }) => {
      const contrast = describeProposalContrast(model)
      const headcount = Math.max(1, model.rows.length)
      return {
        name: String(name || 'Plan'),
        companyCost: model.totals.employerAllocation,
        ownerShare: contrast.value('employerAllocation'),
        ownerSharePercent: model.totals.employerAllocation > 0
          ? contrast.value('employerAllocation') / model.totals.employerAllocation * 100 : 0,
        everyoneElseShare: contrast.restValue('employerAllocation'),
        percentOfPay: model.totals.pay > 0 ? model.totals.employerAllocation / model.totals.pay * 100 : 0,
        perEmployee: Math.round(model.totals.employerAllocation / headcount),
        employeeSavings: model.totals.own401k,
        totalIntoPlan: model.totals.totalRetirementAllocation,
        estimatedTaxSavings: model.summary.estimatedTaxSavings,
        highlightLabel: contrast.highlightLabel,
        headcount: model.rows.length,
        includesCashBalance: model.totals.cashBalance > 0,
      }
    })
  return { available: columns.length > 0, columns, multiple: columns.length > 1 }
}

export function buildProposalReportModel({
  calculatedEmployees = [],
  plan = {},
  enginePlan = {},
  metrics = {},
  reportConfig = {},
  planYear = null,
} = {}) {
  const proposal = isObject(plan?.newCompProposal) ? plan.newCompProposal : null
  const settings = resolveProposalReportSettings(reportConfig, proposal)
  const fundingCorridor = normalizeFundingCorridor(proposal)
  const included = getEmployeesForMetrics(calculatedEmployees, plan?.terminationHandling || {})
  const rows = roleRows(included.map(employeeRow), proposal)
  const owners = rows.filter((row) => row.role === 'owner')
  const nonOwners = rows.filter((row) => row.role !== 'owner')
  const total = subtotal(rows)
  const ownerTotal = subtotal(owners)
  const teamTotal = subtotal(nonOwners)
  const taxRate = settings.marginalTaxRatePercent / 100
  const hasCashBalance = total.cashBalance > 0 || metrics?.cashBalanceEnabled === true
  const deductibleContribution = hasCashBalance
    ? (fundingCorridor.recommendedAvailable ? total.dcEmployer + fundingCorridor.recommended : null)
    : total.dcEmployer
  const groups = [
    // Client-facing labels: a business owner reading the report should not have
    // to know what a "target" or an "HCE" is. The pay threshold behind "highly
    // paid" is stated once, where the groups are first shown.
    { key: 'owners', label: 'Owners', rows: owners },
    { key: 'hces', label: 'Other highly paid employees', rows: rows.filter((row) => row.role === 'hce') },
    { key: 'team', label: 'Everyone else', rows: rows.filter((row) => row.role === 'team') },
  ].filter((group) => group.rows.length).map((group) => ({ ...group, subtotal: subtotal(group.rows) }))
  const alternatives = [
    alternative({ id: 'base', label: 'Match & safe harbor only', rows, includeProfitSharing: false, includeCashBalance: false, taxRate, funding: fundingCorridor }),
    alternative({ id: 'profit-sharing', label: 'Plus profit sharing', rows, includeProfitSharing: true, includeCashBalance: false, taxRate, funding: fundingCorridor }),
    ...(total.cashBalance > 0
      ? [alternative({ id: 'cash-balance', label: 'Plus cash balance', rows, includeProfitSharing: true, includeCashBalance: true, taxRate, funding: fundingCorridor })]
      : []),
  ]
  return {
    available: rows.length > 0 && (total.employerAllocation > 0 || total.own401k > 0),
    settings,
    fundingCorridor,
    rows,
    groups,
    sources: sourceBreakdown(rows),
    totals: total,
    ownerTotals: ownerTotal,
    teamTotals: teamTotal,
    summary: {
      totalRetirementAllocation: total.totalRetirementAllocation,
      modeledEmployerAllocation: total.employerAllocation,
      ownerRetirementAllocation: ownerTotal.totalRetirementAllocation,
      ownerEmployerAllocation: ownerTotal.employerAllocation,
      teamEmployerCost: teamTotal.employerAllocation,
      teamRecipientCount: nonOwners.filter((row) => row.employerAllocation > 0).length,
      ownerSharePercent: total.employerAllocation > 0 ? ownerTotal.employerAllocation / total.employerAllocation * 100 : 0,
      ownerDollarsPerTeamDollar: teamTotal.employerAllocation > 0
        ? ownerTotal.employerAllocation / teamTotal.employerAllocation : null,
      deductibleContribution,
      estimatedTaxSavings: deductibleContribution === null ? null : deductibleContribution * taxRate,
      estimatedAfterTaxCost: deductibleContribution === null ? null : deductibleContribution * (1 - taxRate),
      taxBasis: hasCashBalance
        ? (fundingCorridor.recommendedAvailable ? 'dc-plus-actuary-recommended-funding' : 'requires-actuary-funding')
        : 'modeled-dc-employer-cost',
    },
    projections: projectionRows({ owners, settings, enginePlan, planYear }),
    alternatives,
  }
}
